import dayjs from "dayjs";

export const FORMAT_DATE_SIMPLE_MONTH = "YYYYMM";
export const FORMAT_DATE_SIMPLE = "YYYYMMDD";
export const FORMAT_DATE_YEAR_CN = "YYYY年";
export const FORMAT_DATE_MONTH_CN = "YYYY年MM月";
export const FORMAT_DATE_DAY_CN = "YYYY年MM月DD日";
export const FORMAT_DATE_MONTH = "YYYY-MM";
export const FORMAT_DATE_DAY = "YYYY-MM-DD";
export const FORMAT_DATE_ORDER = "YYYY.MM.DD HH:mm:ss";
export const FORMAT_DATE_NORMAL = "YYYY-MM-DD HH:mm:ss";
export const FORMAT_DATE_SMALL = "YYYYMMDDHHmmss";
export const FORMAT_DATE_DRAW = "YYYY年MM月DD日 HH:mm:ss";

export interface TimeInfo {
  day?: number;
  hour?: number;
  minute?: number;
  second?: number;
}

// 时间字段
const timeFields = ["day", "hour", "minute", "second"];
// 最小单位数组
const timeBox = [1000 * 60 * 60 * 24, 1000 * 60 * 60, 1000 * 60, 1000];
// 初始化时间对象
export const initIntervalData = () =>
  timeFields.reduce((t, o) => (t[o] = 0) || t, {});
/**
 * e.g.  duration: 6000 ---> { day: 0, hour: 0, minute: 0, second: 6 }
 * @param duration 间隔时长
 * @return TimeInfo
 */
export const handlerDurationTime = (duration: number = 0): TimeInfo => {
  // 返回对象 初始化 { day: 0, hour: 0, minute: 0, second: 0 }
  let res = initIntervalData();

  const handler = (next: number, index: number): TimeInfo => {
    if (index > timeFields.length - 1 || next <= 0) return res;
    let timeField = timeFields[index],
      timeNumber = timeBox[index];
    if (next - timeNumber >= 0) {
      res[timeField] = Math.floor(next / timeNumber);
      return handler(next - res[timeField] * timeNumber, index + 1);
    }
    return handler(next, index + 1);
  };
  return handler(duration, 0);
};

export const formatPromotionData = (timeData: TimeInfo = {}) => {
  const { day, hour, minute, second } = timeData;
  return `${day}天 ${hour}时 ${minute}分 ${second}秒`;
};

/**
 * 时间字符串，转，timestamp
 */
export const strToTime = (
  str: string,
  formatStr: string = FORMAT_DATE_NORMAL
): number => {
  return dayjs(str, formatStr).valueOf();
};

/**
 * 验证时间开始、结束
 * @param time
 * @param formatStr
 */
export const verifyTime = (
  time: string | number,
  formatStr?: string | undefined
): boolean => {
  const now = Date.now();
  return typeof time === "number"
    ? time < now
    : strToTime(time as string, formatStr) <= now;
};

/**
 * 活动是否开始 true 已开始， false 未开始
 * @param begin string : 时间格式化的字符串，number : 时间戳， 如果是string 需要反格式化为时间戳
 * @param formatStr 格式化字符串
 */
export const isBegin = (
  begin: string | number,
  formatStr?: string | undefined
): boolean => {
  if (!begin) return false;
  return verifyTime(begin, formatStr);
};

/**
 * 活动是否结束 true 已结束， false 没有结束
 * @param end string : 时间格式化的字符串，number : 时间戳， 如果是string 需要反格式化为时间戳
 * @param formatStr 格式化字符串
 */
export const isFinish = (
  end: string | number,
  formatStr?: string | undefined
): boolean => {
  if (!end) return true;
  return verifyTime(end, formatStr);
};

export const formatDateMap = Object.freeze({
  normal: FORMAT_DATE_NORMAL,
  monthCN: FORMAT_DATE_MONTH_CN,
  day: FORMAT_DATE_DAY,
  simpleMonth: FORMAT_DATE_SIMPLE_MONTH,
  small: FORMAT_DATE_SMALL,
  month: FORMAT_DATE_MONTH
});

/**
 * 格式化时间工厂
 */
export const DateFormatClass = (() => {
  const res = Object.create(null);
  for (let type in formatDateMap) {
    res[`${type}`] = (time: any) => dayjs(time).format(formatDateMap[type]);
  }
  return res;
})();

/**
 * 解析时间工厂
 */
export const DateClass = (() => {
  const res = Object.create(null);
  for (let type in formatDateMap) {
    res[`${type}`] = (time: any) => dayjs(time, formatDateMap[type]);
  }
  return res;
})();

/**
 * YYYY-MM-DD HH:mm:ss ==> YYYY年MM月
 * @param date
 */
export const formatMonthCNDate = (date: any) => {
  return DateFormatClass.monthCN(DateClass.normal(date));
};

/**
 * YYYY-MM-DD HH:mm:ss ==> YYYY-MM-DD
 * @param date
 */
export const formatDayDate = (date: any) => {
  return DateFormatClass.day(DateClass.normal(date));
};

/**
 * YYYY-MM-DD HH:mm:ss ==> YYYY-MM
 * @param date
 */
export const formatMonthDate = (date: any) => {
  return DateFormatClass.month(DateClass.month(date));
};

/**
 * YYYY-MM-DD HH:mm:ss ==> YYYYMM
 * @param date
 */
export const formatSimpleMonthDate = (date: any) => {
  return DateFormatClass.simpleMonth(DateClass.normal(date));
};

/**
 * 格式化时间
 * @param date 时间
 * @param format 转换的格式
 */
export const formatByDate = (date: Date | string, format?: string): string =>
  dayjs(date).format(format ?? FORMAT_DATE_NORMAL);

/**
 * 获取当前时间之前36个月的时间段
 */
export const getDateBefore36 = () => {
  const current = dayjs().endOf("date");
  const before = current.subtract(36, "month");
  return [before.toDate(), current.toDate()];
};
